import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, FileCsv, ChartLineUp } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function getToken() { return localStorage.getItem("rescigo_token") || ""; }

function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function BreakActivityLogs() {
  const [staff, setStaff] = useState([]);
  const [sid, setSid] = useState("");
  const [rows, setRows] = useState([]);
  // Iter 103 — reporting controls
  const [preset, setPreset] = useState("month");     // today | week | month | custom
  const [start, setStart] = useState(daysAgoIso(30));
  const [end, setEnd] = useState(todayIso());
  const [summary, setSummary] = useState(null);      // totals for the picked staff+range
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/office-staff").then(r => {
      setStaff(r.data);
      if (r.data.length && !sid) setSid(r.data[0].id);
    }).catch(e => toast.error(formatApiError(e)));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!sid) return;
    api.get(`/office-staff/${sid}/activity-logs?limit=300`).then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)));
  }, [sid]);

  // Recompute the report window when preset changes
  useEffect(() => {
    if (preset === "today")  { setStart(todayIso());       setEnd(todayIso()); }
    else if (preset === "week")   { setStart(daysAgoIso(7)); setEnd(todayIso()); }
    else if (preset === "month")  { setStart(daysAgoIso(30)); setEnd(todayIso()); }
    // "custom" leaves the dates as-is
  }, [preset]);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (sid) p.set("user_id", sid);
    p.set("start_date", start + "T00:00:00");
    p.set("end_date", end + "T23:59:59");
    return p;
  };

  const loadSummary = async () => {
    setBusy(true);
    try {
      const params = buildParams(); params.set("format", "json");
      const r = await api.get(`/workforce/report/activity?${params}`);
      setSummary(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadReport = async (format) => {
    setBusy(true);
    try {
      const params = buildParams(); params.set("format", format);
      const r = await fetch(`${BASE}/workforce/report/activity?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `activity_${start}_${end}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) { toast.error("Download failed"); }
    finally { setBusy(false); }
  };

  const dutyEvents = rows.filter(l => (l.action || "").startsWith("my_duty") || (l.action || "").startsWith("office_staff"));
  const timeTotal = summary && sid ? summary.time_totals?.[sid] : null;
  const buckets = summary && sid
    ? (summary.activity?.rows || []).find(r => r.user_id === sid)?.buckets
    : null;

  return (
    <AppShell title="Break & Activity Logs" subtitle="Full audit trail · immutable"
      actions={
        <select value={sid} onChange={e => setSid(e.target.value)} data-testid="log-staff-picker"
                className="h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
          {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
        </select>
      }>
      {/* Iter 103 — report controls */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-4"
           data-testid="report-controls">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1">
            {["today","week","month","custom"].map(p => (
              <button key={p} type="button" onClick={() => setPreset(p)}
                      data-testid={`preset-${p}`}
                      className={`h-9 px-3 rounded-md text-xs border transition-colors ${
                        preset === p ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                     : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#2D5A27]"
                      }`}>{p === "week" ? "7 days" : p === "month" ? "30 days" : p[0].toUpperCase()+p.slice(1)}</button>
            ))}
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-[0.2em] text-[#5C5A55]">From</label>
            <input type="date" value={start} data-testid="report-from"
                    onChange={e => { setStart(e.target.value); setPreset("custom"); }}
                    className="h-9 px-2 border border-[#E5E2D9] rounded-md text-sm block"/>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-[0.2em] text-[#5C5A55]">To</label>
            <input type="date" value={end} data-testid="report-to"
                    onChange={e => { setEnd(e.target.value); setPreset("custom"); }}
                    className="h-9 px-2 border border-[#E5E2D9] rounded-md text-sm block"/>
          </div>
          <button onClick={loadSummary} disabled={busy}
                  data-testid="report-load"
                  className="h-9 px-3 border border-[#2D5A27] text-[#2D5A27] rounded-md text-sm hover:bg-[#F0F7EE]">
            <ChartLineUp size={13} className="inline mr-1"/> Summarise
          </button>
          <div className="flex-1"/>
          <button onClick={() => downloadReport("pdf")} disabled={busy}
                  data-testid="report-pdf"
                  className="h-9 px-3 bg-[#7A2E18] hover:bg-[#5A1F10] text-white rounded-md text-xs font-semibold inline-flex items-center gap-1.5">
            <FilePdf size={13} weight="fill"/> PDF
          </button>
          <button onClick={() => downloadReport("csv")} disabled={busy}
                  data-testid="report-csv"
                  className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs font-semibold inline-flex items-center gap-1.5">
            <FileCsv size={13} weight="fill"/> CSV
          </button>
        </div>

        {summary && (
          <div className="mt-4" data-testid="report-summary">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
              Summary · {summary.label}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <SumStat label="Working hours" value={timeTotal ? (timeTotal.active_ms/3600000).toFixed(1) : "0"}/>
              <SumStat label="Break minutes" value={timeTotal ? Math.round(timeTotal.break_ms/60000) : 0}/>
              <SumStat label="Idle minutes" value={timeTotal ? Math.round(timeTotal.inactive_ms/60000) : 0}/>
              <SumStat label="Days worked" value={timeTotal?.days ?? 0}/>
            </div>
            {buckets && Object.values(buckets).some(v=>v>0) && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {Object.entries(buckets).filter(([,v]) => v>0).map(([k,v]) => (
                  <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0F7EE] text-[#1A3317] border border-[#B5D5AC]">
                    {k}: <b>{v}</b>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md" data-testid="activity-logs">
        <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex justify-between">
          <span>All activity · {rows.length} records</span>
          <span>Duty events: {dutyEvents.length}</span>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {rows.length === 0 && <div className="px-5 py-8 text-xs text-center text-[#8A8780]">No activity logged.</div>}
          {rows.map(l => {
            const isDuty = (l.action || "").startsWith("my_duty");
            return (
              <div key={l.id} className={`px-5 py-3 border-t border-[#E5E2D9] flex items-center justify-between ${isDuty ? "bg-[#F7F6F2]" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-mono ${isDuty ? "text-[#2D5A27] font-semibold" : "text-[#5C5A55]"}`}>{l.action}</div>
                  {l.target && <div className="text-[10px] text-[#8A8780] font-mono break-all mt-0.5">target: {l.target}</div>}
                  {l.meta && Object.keys(l.meta).length > 0 && (
                    <div className="text-[10px] text-[#8A8780] font-mono mt-0.5 truncate">{JSON.stringify(l.meta)}</div>
                  )}
                </div>
                <div className="text-[10px] text-[#8A8780] ml-4 shrink-0">{new Date(l.timestamp).toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}

function SumStat({ label, value }) {
  return (
    <div className="rounded-md border border-[#E5E2D9] bg-[#FAFAF7] px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[#8A8780]">{label}</div>
      <div className="text-lg font-heading text-[#1A1A1A]">{value}</div>
    </div>
  );
}
